import { AppShellSkeleton } from '@/components/core';
import { useGetMultiOrgs } from '@/lib/api/hooks/use-multi-orgs';
import {
  DEFAULT_ORGANIZATION_ID,
  recoverOrgAccessBySwitchingToDefault,
  shouldRecoverOrgAccess,
} from '@/lib/auth/org-access';
import { useGetAccount, useGetMyPermissions } from '@/modules/profile/hooks/use-account';
import { useAuthStore } from '.';
import { useEffect, useRef } from 'react';
import { useErrorHandler } from '@/hooks/use-error-handler';

/** What `me` reports plus what IAM's features endpoint says the user holds in this organization. */
const mergePermissions = (own: string[] | undefined, fromRoles: string[] | undefined) =>
  Array.from(new Set([...(own ?? []), ...(fromRoles ?? [])]));

export const Guard = ({ children }: { children: React.ReactNode }) => {
  const { data, isSuccess, error } = useGetAccount();
  const {
    data: rolePermissions,
    isPending: isRolePermissionsPending,
    fetchStatus: rolePermissionsFetchStatus,
  } = useGetMyPermissions(data?.organizationId, isSuccess);
  const { setUser, setSelectedOrgId, isAuthenticated, selectedOrgId } = useAuthStore();
  // Only hold the user back while the permission lookup is actually in flight; a failed lookup
  // falls back to the permissions `me` returned.
  const isResolvingRolePermissions =
    isRolePermissionsPending && rolePermissionsFetchStatus !== 'idle';
  const { handleError } = useErrorHandler();
  const {
    data: orgsData,
    error: orgsError,
    isSuccess: isOrgsSuccess,
  } = useGetMultiOrgs({
    enabled: isAuthenticated && isSuccess,
  });
  const lastErrorRef = useRef<any>(null);
  const isRecoveringOrgAccessRef = useRef(false);

  // The active organization is the one `me` reports, read here once per load; every organization-scoped
  // query and check reads it from the store. An account without one is in the built-in default organization.
  const accountOrgId = isSuccess ? data?.organizationId || DEFAULT_ORGANIZATION_ID : null;
  useEffect(() => {
    if (accountOrgId) setSelectedOrgId(accountOrgId);
  }, [accountOrgId, setSelectedOrgId]);

  useEffect(() => {
    if (error && error !== lastErrorRef.current) {
      lastErrorRef.current = error;
      handleError(error);
      return;
    }

    if (!isAuthenticated || !isSuccess || isResolvingRolePermissions) return;
    setUser(
      data ? { ...data, permissions: mergePermissions(data.permissions, rolePermissions) } : null
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    data,
    rolePermissions,
    isResolvingRolePermissions,
    isAuthenticated,
    isSuccess,
    error,
    setUser,
  ]);

  useEffect(() => {
    if (
      !isAuthenticated ||
      !isSuccess ||
      isRecoveringOrgAccessRef.current ||
      !selectedOrgId ||
      selectedOrgId === DEFAULT_ORGANIZATION_ID
    ) {
      return;
    }

    const canEvaluateOrgAccess = Boolean(orgsError) || isOrgsSuccess;
    if (!canEvaluateOrgAccess) {
      return;
    }

    if (!shouldRecoverOrgAccess(selectedOrgId, orgsError, orgsData)) {
      return;
    }

    isRecoveringOrgAccessRef.current = true;
    void recoverOrgAccessBySwitchingToDefault().catch(() => {
      isRecoveringOrgAccessRef.current = false;
    });
  }, [isAuthenticated, isSuccess, orgsError, isOrgsSuccess, orgsData, selectedOrgId]);

  // Pages query on render, and a query without the organization would read every organization's records.
  if (isAuthenticated && !error && !selectedOrgId) return <AppShellSkeleton />;

  return <>{children}</>;
};
